/**
 * Derived-coverage scoring — the formula behind the three `derivation_kind =
 * 'derived-coverage'` criteria seeded in 0040.
 *
 * Score is LINEAR:  (Σ fit_weight) / (|in_scope|) × 5
 *   Native = 1.0, Plugin = 0.7, Custom = 0.4
 * Returns null when no features match the filter (distinct from 0 — "not
 * assessed" vs "evaluated zero"). The dashboard uses null to show the
 * "0 features assigned" copy rather than dragging the weighted total down.
 *
 * `domain_breadth` mode is a different math: distinct domains a product has
 * any assignment in, divided by 8, times 5.
 */

import type {
  CoverageFilter,
  EvaluationCriterionRow,
  FeatureDomain,
  FeatureProductCoverage,
  FeatureProductRow,
  FeatureRow,
  FeatureRoadmapVerdictRow,
} from "@/infrastructure/supabase/database.types";

const FIT_WEIGHT: Record<FeatureProductCoverage, number> = {
  native: 1.0,
  plugin: 0.7,
  custom: 0.4,
  // `irrelevant` is an explicit "we considered this and it doesn't apply"
  // marker — counted as assessed (so the product isn't shown as "not assessed")
  // but contributes zero weight to the coverage ratio.
  irrelevant: 0.0,
};

export interface DerivedCoverageInputs {
  features: FeatureRow[];
  productAssignments: FeatureProductRow[];
  verdicts: FeatureRoadmapVerdictRow[];
}

export interface DerivedCoverageResult {
  score: number | null;
  ratio: number | null;
  inScopeCount: number;
  nativeCount: number;
  pluginCount: number;
  customCount: number;
  weightedSum: number;
  /** For `domain_breadth` mode: distinct domains the product touches. */
  distinctDomains?: number;
}

function matchesFilter(
  feature: FeatureRow,
  filter: CoverageFilter,
  verdicts: FeatureRoadmapVerdictRow[]
): boolean {
  if (filter.journey && filter.journey.length > 0) {
    if (!feature.journey) return false;
    const fj = feature.journey as "B2C" | "B2B" | "both";
    if (!filter.journey.includes(fj)) return false;
  }
  if (filter.exclude_triage && feature.triage_status) {
    if (filter.exclude_triage.includes(feature.triage_status)) return false;
  }
  if (filter.require_verdict) {
    const { branch, verdict } = filter.require_verdict;
    const featureVerdict = verdicts.find((v) => v.feature_id === feature.id && v.branch === branch);
    if (!featureVerdict) return false;
    if (!verdict.includes(featureVerdict.verdict)) return false;
  }
  return true;
}

export function computeDerivedCoverage(
  criterion: EvaluationCriterionRow,
  inputs: DerivedCoverageInputs
): DerivedCoverageResult {
  const filter: CoverageFilter = criterion.coverage_filter ?? {};

  // Domain-breadth mode: count distinct domains the product touches across
  // all its assignments (no per-feature filter applies — by definition the
  // mode IS the filter).
  if (filter.mode === "domain_breadth") {
    const featureById = new Map(inputs.features.map((f) => [f.id, f]));
    const touchedDomains = new Set<FeatureDomain>();
    for (const a of inputs.productAssignments) {
      const f = featureById.get(a.feature_id);
      if (!f || !f.domain) continue;
      touchedDomains.add(f.domain);
    }
    const distinctDomains = touchedDomains.size;
    const ratio = distinctDomains / 8;
    return {
      score: ratio * 5,
      ratio,
      inScopeCount: 8,
      nativeCount: 0,
      pluginCount: 0,
      customCount: 0,
      weightedSum: 0,
      distinctDomains,
    };
  }

  // Standard weighted-ratio mode
  const inScopeFeatures = inputs.features.filter((f) => matchesFilter(f, filter, inputs.verdicts));
  const inScopeIds = new Set(inScopeFeatures.map((f) => f.id));
  const relevantAssignments = inputs.productAssignments.filter((a) => inScopeIds.has(a.feature_id));

  if (inScopeFeatures.length === 0) {
    return {
      score: null,
      ratio: null,
      inScopeCount: 0,
      nativeCount: 0,
      pluginCount: 0,
      customCount: 0,
      weightedSum: 0,
    };
  }

  let nativeCount = 0;
  let pluginCount = 0;
  let customCount = 0;
  let weightedSum = 0;
  for (const a of relevantAssignments) {
    weightedSum += FIT_WEIGHT[a.coverage];
    if (a.coverage === "native") nativeCount += 1;
    else if (a.coverage === "plugin") pluginCount += 1;
    else if (a.coverage === "custom") customCount += 1;
    // 'irrelevant' contributes 0 weight and isn't surfaced in any of the
    // native/plugin/custom counts — it's tracked only by absence from these.
  }

  // Zero in-scope assignments → null. Distinguishes "we evaluated this
  // product against zero features in scope" from "we have explicit zero-fit
  // assignments". With our 4-state model (absent / native / plugin / custom),
  // we never store a 0-fit row — absent = 0-fit. So "no relevant assignments"
  // means "not assessed yet" → null score.
  if (relevantAssignments.length === 0) {
    return {
      score: null,
      ratio: null,
      inScopeCount: inScopeFeatures.length,
      nativeCount: 0,
      pluginCount: 0,
      customCount: 0,
      weightedSum: 0,
    };
  }

  const ratio = weightedSum / inScopeFeatures.length;
  return {
    score: ratio * 5,
    ratio,
    inScopeCount: inScopeFeatures.length,
    nativeCount,
    pluginCount,
    customCount,
    weightedSum,
  };
}
